'use client';

import { motion } from 'motion/react';
import type { Variants } from 'motion/react';
import {
  RiBarChartBoxLine,
  RiCheckDoubleLine,
  RiPlugLine,
  RiShieldKeyholeLine,
} from '@remixicon/react';
import { useTranslations } from 'next-intl';

import { AuthAsideRays } from '@/components/auth-aside-rays';
import { dur, ease, spring, springFade, stagger } from '@/lib/motion';

/**
 * Kayıt ekranının koyu yarısı — "kayıttan sonra ne oluyor" paneli.
 *
 * Neden bu içerik: giriş ekranının paneli DÖNEN kullanıcıya "siz yokken de
 * çalıştı" diyor. Burada dönen kullanıcı yok; buradaki kişi ürünü hiç
 * görmemiş ve tek bir şeyi merak ediyor — "bu formu doldurursam ne olacak,
 * ne kadar sürecek?" Panel o soruyu cevaplıyor: üç adım, yanlarında süreleri.
 *
 * Rakamlar uydurma değil, sitenin başka yerlerinde verilen sözlerle aynı
 * (SSS: "ortalama beş dakika", "ilk rapor 15 dakika içinde"). İki yerde iki
 * farklı süre yazsaydı ikisi de inandırıcılığını kaybederdi.
 *
 * ── Hareket neyi açıklıyor ────────────────────────────────────────────────
 *
 * Adımlar sırayla giriyor ve aralarındaki dikey çizgi YUKARIDAN AŞAĞI çiziliyor
 * (`scaleY`, origin top). Çizgi bir süs değil: zamanın aktığı yönü gösteriyor,
 * adımların birbirine bağlı olduğunu söylüyor. Üçüncü adımda çizgi yok — dizi
 * orada bitiyor, bir sonrakine bağlanmıyor.
 *
 * Zaman etiketleri adımlarla birlikte geliyor, ayrı bir hareketleri yok:
 * "2 dk" bir olay değil, adımın sıfatı.
 *
 * Sonsuz döngü yok — bu yüzden `useReducedMotion()` çağrısı da yok. Kök
 * layout'taki `<MotionConfig reducedMotion="user">` giriş hareketlerini zaten
 * kapatıyor (DESIGN.md §1).
 */

const STEPS = [
  { key: 'connect', Icon: RiPlugLine },
  { key: 'analyze', Icon: RiBarChartBoxLine },
  { key: 'approve', Icon: RiCheckDoubleLine },
] as const;

const asideV: Variants = {
  hidden: {},
  /* Sol sütun bir tık önce başlıyor: göz önce forma düşsün, panel arkadan
     gelsin. Aynı anda başlayan iki sütun dikkati ikiye bölüyordu. */
  show: { transition: { staggerChildren: stagger.base, delayChildren: 0.18 } },
};

const asideItem: Variants = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

/* Adımlar kendi aralarında daha geniş aralıklı: üç eleman, her biri okunacak
   bir cümle taşıyor. `stagger.tight` olsaydı üçü birlikte gelirdi. */
const stepsV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.loose, delayChildren: 0.1 } },
};

const stepV: Variants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

/* Çizgi adımın METNİNDEN sonra iniyor: önce durak, sonra ona giden yol. */
const lineV: Variants = {
  hidden: { scaleY: 0 },
  show: { scaleY: 1, transition: { duration: dur.slow, ease: ease.out, delay: 0.12 } },
};

export function RegisterAside() {
  const t = useTranslations('register.aside');

  return (
    /* aria-hidden DEĞİL: içerik gerçek bilgi taşıyor. Ama formdan SONRA
       okunsun diye DOM'da sonra duruyor — görsel sıra da aynı. */
    <aside className="auth-aside">
      {/* Zemin giriş ekranıyla ORTAK (components/auth-aside-rays.tsx). Aynı
          bileşen: kullanıcı giriş ile kayıt arasında gidip geliyor, iki panelin
          zemini birbirinden ayrılırsa fark doğrudan görünür. Buradaki ızgara da
          o yüzden kalktı — giriş ekranında kalkmıştı. */}
      <AuthAsideRays />

      <motion.div className="auth-aside-inner" variants={asideV} initial="hidden" animate="show">
        <motion.p className="auth-aside-eyebrow" variants={asideItem}>
          {t('eyebrow')}
        </motion.p>

        <motion.h2 className="auth-aside-title" variants={asideItem}>
          {t('title')}
        </motion.h2>

        <motion.p className="auth-aside-lead" variants={asideItem}>
          {t('lead')}
        </motion.p>

        <motion.ol className="auth-steps" variants={stepsV}>
          {STEPS.map(({ key, Icon }, i) => (
            <motion.li key={key} className="auth-step" variants={stepV}>
              {/* Ray: ikon + altındaki çizgi. İkon kutu içinde DEĞİL, doğrudan
                  zemine oturuyor (DESIGN.md §3) — çizgi ikonun altından
                  başlıyor, arkasından geçmiyor. */}
              <span className="auth-step-rail" aria-hidden="true">
                <Icon size={18} className="auth-step-icon" />
                {i < STEPS.length - 1 && (
                  <motion.span className="auth-step-line" variants={lineV} />
                )}
              </span>

              <div className="auth-step-body">
                <p className="auth-step-head">
                  <span className="auth-step-title">{t(`steps.${key}.title`)}</span>
                  <span className="auth-step-time">{t(`steps.${key}.time`)}</span>
                </p>
                <p className="auth-step-text">{t(`steps.${key}.text`)}</p>
              </div>
            </motion.li>
          ))}
        </motion.ol>

        <motion.p className="auth-aside-note" variants={asideItem}>
          <RiShieldKeyholeLine size={16} aria-hidden="true" />
          {t('note')}
        </motion.p>
      </motion.div>
    </aside>
  );
}
